import React, {useCallback, useMemo} from 'react'
import {Button, ButtonIcon, Col, Row} from 'grilnica-reactstrap'
import {arrowLeftSVG, closeSVG} from '../../../../../components/svg/SystemIcons'
import {HtmlView} from 'grilnica-ckeditor'
import {
  MenuCategoryDTO,
  ProductMap,
  PushClientDTO,
  RestaurantByProductMap,
} from 'grilnica-store-share'
import ProductItem from '../../../menu/components/product/productList/productItem/ProductItem'
import {getUrl} from '../../../../utils/search/getUrl'
import {ProductDTO} from 'grilnica-store-share/lib/product/ProductDTO'
import {formatDate} from 'grilnica-share'

interface NotificationPageInfoProps {
  message: PushClientDTO

  categoryList: MenuCategoryDTO[]
  productMap: ProductMap
  restaurantByProductMap: RestaurantByProductMap
  checkIsAddProduct: (productProp: ProductDTO) => ProductDTO

  hidePushMessage: () => void
  goBack: () => void
  colSize: any
}

const NotificationPageInfo: React.FC<NotificationPageInfoProps> = ({
  message,
  categoryList,
  productMap,
  restaurantByProductMap,
  checkIsAddProduct,
  hidePushMessage,
  goBack,
  colSize,
}): React.ReactElement => {
  const renderButtonHide: React.ReactElement = useMemo(() => {
    if (!message.isHidden) {
      return (
        <Button
          size={'sm'}
          style={'text'}
          color={'primary'}
          iconPosition={'right'}
          iconPath={closeSVG}
          isRoundedButton
          onClick={() => {
            hidePushMessage()
          }}
          className={'ml-x2'}>
          Скрыть
        </Button>
      )
    } else {
      return null
    }
  }, [hidePushMessage, message.isHidden])

  const getBodyMarkupItems = useCallback(
    (bodyMarkupRN: string) => {
      const items: React.ReactNode[] = []

      let match = bodyMarkupRN.match(/\[product .{36}\]/)
      if (
        match &&
        match?.length > 0 &&
        message.productsInBodyMarkup &&
        message.productsInBodyMarkup?.length > 0
      ) {
        let keyIndex: number = 0
        let index: number = 0
        let nextIndex: number = bodyMarkupRN.indexOf(match[0])
        if (nextIndex !== -1) {
          while (nextIndex !== -1) {
            if (items.length > 1) {
              const br = bodyMarkupRN.substring(index, index + 5)
              //Убираем лишний перенос
              if (br === '<br/>') {
                index += 5
              }
            }
            const str = bodyMarkupRN.substring(index, nextIndex).trim()
            if (str?.length > 0) {
              items.push(
                <Col xl={12} lg={12} md={12} sm={12} xs={12} key={'jsx-' + keyIndex}>
                  <HtmlView html={`<div>${str}</div>`} />
                </Col>,
              )
              keyIndex++
            }

            let productStr = bodyMarkupRN.substring(nextIndex, nextIndex + match[0].length)
            if (productStr) {
              for (const productInBodyMarkup of message.productsInBodyMarkup) {
                if (productStr === `[product ${productInBodyMarkup.productId}]`) {
                  const product = checkIsAddProduct(productInBodyMarkup)
                  if (product) {
                    let isNewPrice: boolean = false
                    if (productInBodyMarkup?.price || productInBodyMarkup?.salePrice) {
                      if (productInBodyMarkup?.price) {
                        isNewPrice = productInBodyMarkup?.price !== product.price
                      }
                      if (productInBodyMarkup?.salePrice) {
                        isNewPrice = productInBodyMarkup?.salePrice !== product.salePrice
                      }
                    }

                    //in-body-product-wrapper
                    items.push(
                      <ProductItem
                        key={product.productId}
                        product={product}
                        productMap={productMap}
                        restaurantByProductMap={restaurantByProductMap}
                        url={getUrl(product, categoryList || [])}
                        menuCategoryList={categoryList}
                        colSize={colSize}
                        pushQueueCoupon={message?.coupon ? message?.coupon : undefined}
                        pushQueueId={message.pushQueueId}
                        isSendToBasketCombo //Отправка в корзину комбо а не открываем страницу
                        isAddCouponWithProduct //Таже добовляем промокод в корзину
                        isHiddenOptionsAndVariant //Скрыть отображение опций и вариантов товаров
                        defaultProductValueMap={{
                          weight: undefined,
                          priceMap: isNewPrice
                            ? {
                                price: productInBodyMarkup?.price,
                                salePrice: productInBodyMarkup?.salePrice,
                              }
                            : undefined,
                        }}
                      />,
                    )
                  }
                  keyIndex++
                  break
                }
              }
            }

            index = nextIndex + match[0].length

            const nextBodyMarkupRN = bodyMarkupRN.substring(index)

            match = (nextBodyMarkupRN || '').match(/\[product .{36}\]/)

            if (match && match[0]) {
              nextIndex = bodyMarkupRN.indexOf(match[0], index)
            } else {
              nextIndex = -1
            }
          }

          if (items.length > 1) {
            const br = bodyMarkupRN.substring(index, index + 5)
            //Убираем лишний перенос
            if (br === '<br/>') {
              index += 5
            }
          }

          const endStr = bodyMarkupRN.substring(index).trim()
          if (endStr?.length > 0) {
            items.push(
              <Col xl={12} lg={12} md={12} sm={12} xs={12} key={'jsx-' + keyIndex}>
                <HtmlView html={`<div>${endStr}</div>`} />
              </Col>,
            )
          }
        }
      } else {
        items.push(
          <Col xl={12} lg={12} md={12} sm={12} xs={12} key={'jsx-not-products'}>
            <HtmlView html={`<div>${bodyMarkupRN}</div>`} />
          </Col>,
        )
      }

      return items
    },
    [
      categoryList,
      checkIsAddProduct,
      colSize,
      message?.coupon,
      message?.productsInBodyMarkup,
      message?.pushQueueId,
      productMap,
      restaurantByProductMap,
    ],
  )

  const renderBody: React.ReactElement = useMemo(() => {
    let renderBodyText: React.ReactNode | any
    if (message?.bodyMarkup) {
      const newBodyMarkup = message?.bodyMarkup.replace(/\n/gi, '<br/>')
      const items: React.ReactNode[] = getBodyMarkupItems(newBodyMarkup)

      renderBodyText = <Row>{items}</Row>
    } else {
      renderBodyText = <HtmlView html={`<div>${message?.body}</div>`} />
    }
    return <div className={'notification-page-info-body'}>{renderBodyText}</div>
  }, [getBodyMarkupItems, message?.body, message?.bodyMarkup])

  const renderContent: React.ReactElement = useMemo(() => {
    return (
      <div className={'notification-page-info'}>
        <div className={'notification-page-info-header-wrapper'}>
          <div className={'notification-page-info-header'}>
            <ButtonIcon
              iconPath={arrowLeftSVG}
              isHoverArea
              title={'Назад'}
              className={'mr-x4'}
              onClick={() => {
                goBack()
              }}
            />
            <p className={'notification-page-info-header-p mb-0'}>Уведомления</p>
          </div>
          {renderButtonHide}
        </div>
        <h1 className={'notification-page-info-h1'}>{message.title}</h1>
        {renderBody}
        <p className={'notification-page-info-date'}>{formatDate(message?.factSendTime)}</p>
      </div>
    )
  }, [goBack, message?.factSendTime, message.title, renderBody, renderButtonHide])

  return <>{renderContent}</>
}

export {NotificationPageInfo}
